import type { ReactNode } from 'react';
import { SessionAuth, useSessionContext } from 'supertokens-auth-react/recipe/session';
import { UserRoleClaim } from 'supertokens-auth-react/recipe/userroles';

// Clone of AdminRoute.tsx for the /developer surface, gated on the `api_user`
// role instead of `admin`. Admins must also be let in -- they won't have
// `api_user` in their role list, so the validator checks for *either* role
// rather than using `UserRoleClaim.validators.includes('api_user')` alone
// (which would lock admins out of /developer).
export default function ApiUserRoute(props: { children?: ReactNode }) {
    return (
        <SessionAuth
            overrideGlobalClaimValidators={(globalValidators) => [
                ...globalValidators,
                {
                    id: UserRoleClaim.id,
                    refresh: (userContext) => UserRoleClaim.refresh(userContext),
                    shouldRefresh: (payload, userContext) =>
                        UserRoleClaim.getLastFetchedTime(payload, userContext) === undefined,
                    validate: (payload) => {
                        const roles: string[] = UserRoleClaim.getValueFromPayload(payload) || [];
                        const isValid = roles.includes('api_user') || roles.includes('admin');
                        return isValid
                            ? { isValid: true }
                            : {
                                  isValid: false,
                                  reason: { message: 'not an api_user or admin', roles }
                              };
                    }
                }
                /* PermissionClaim.validators.includes("modify") */
            ]}>
            <InvalidClaimHandler>{props.children}</InvalidClaimHandler>
        </SessionAuth>
    );
}

function InvalidClaimHandler(props: { children?: ReactNode }) {
    const sessionContext = useSessionContext();
    if (sessionContext.loading) {
        return null;
    }

    if (sessionContext.invalidClaims.some((i) => i.id === UserRoleClaim.id)) {
        return (
            <div>
                API access is not enabled on your account. Get in touch with us if you&apos;d
                like access to the API.
            </div>
        );
    }

    // We show the protected route since all claims validators have passed,
    // implying that the user is either an api_user or an admin.
    return <div>{props.children}</div>;
}
